/**
 * Workers-request-form: employer-side form used to create or edit a workers request.
 *
 * This component is intentionally thin: it wires up Formik, delegates the rendering
 * to the three top-level sections, and hands the submission to the `submit-create`
 * / `submit-update` utilities.
 */
import React from 'react'
import { Formik, Form } from 'formik'
import { Element } from 'react-bulma-components'
import { RequestButton, RequestMessage } from '../../../request-components/request-components'
import {
  useCreateMutation,
  useCreatePost,
  useUpdateMutation,
  useUpdatePost,
} from '../../../../queries/workers-requests'
import { useJobTitlesQuery } from '../../../../queries/job-titles'
import useStore from '../../../../store'
import { Employer, Organization } from '../../../../api/organizations'
import MissionDataSection from './sections/mission-data-section'
import WorkPeriodsSection from './sections/work-periods-section'
import PostsSection from './sections/posts-section'
import { getWorkersRequestFormInitialValues } from './utils/workers-request-form-get-initial-values'
import { validateWorkersRequestForm } from './utils/validate'
import { submitCreate } from './utils/submit-create'
import { submitUpdate } from './utils/submit-update'
import {
  WorkersRequestCreateFormValues,
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormProps,
  WorkersRequestUpdateFormValues,
} from './workers-request-form.types'

const WorkersRequestForm: React.FC<WorkersRequestFormProps> = props => {
  const { workersRequest, fromTemplate } = props
  const currentOrganization = useStore(state => state.session.currentOrganization as Organization)

  // All mutations are owned here (single instance per mutation). They are shared
  // between the submit flow (`submitUpdate`) and the in-card `<RequestMessage>` /
  // `<RequestButton>` so loading / success / error states stay in sync. Calling
  // the `useMutation` hook more than once would create independent instances and
  // break the feedback (e.g. a success toast that never appears).
  const createWorkersRequestMutation = useCreateMutation()
  const updateWorkersRequestMutation = useUpdateMutation()
  const updatePostMutation = useUpdatePost()
  const createPostMutation = useCreatePost()

  // The jobTitles cache is passed to the validation pipeline to check the labor
  // coefficient configuration between the selected job title and the interim agency.
  const jobTitlesQuery = useJobTitlesQuery({})

  const isNewWorkersRequest = workersRequest === undefined
  const isExistingWorkersRequest = !isNewWorkersRequest
  const isSubmitDisabled =
    workersRequest &&
    !workersRequest.__actions.canBeUpdated &&
    !workersRequest.__actions.requestedWorkersCanBeUpdated

  const initialValues: WorkersRequestCreateUpdateFormValues =
    getWorkersRequestFormInitialValues(props)

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={values =>
        validateWorkersRequestForm(values, { jobTitles: jobTitlesQuery.data || [] })
      }
      onSubmit={values => {
        if (isExistingWorkersRequest && workersRequest) {
          submitUpdate(
            values as WorkersRequestUpdateFormValues,
            initialValues as WorkersRequestUpdateFormValues,
            {
              workersRequest,
              updateWorkersRequestMutation,
              updatePostMutation,
              createPostMutation,
            },
          )
        } else {
          submitCreate(values as WorkersRequestCreateFormValues, {
            createWorkersRequestMutation,
          })
        }
      }}
    >
      {() => (
        <Form>
          <MissionDataSection workersRequest={workersRequest} fromTemplate={fromTemplate} />
          <WorkPeriodsSection
            workersRequest={workersRequest}
            updateWorkersRequestMutation={updateWorkersRequestMutation}
            isSubmitDisabled={isSubmitDisabled}
          />
          <PostsSection
            workersRequest={workersRequest}
            employer={currentOrganization as Employer}
            initialValues={initialValues}
            isSubmitDisabled={isSubmitDisabled}
            updatePostMutation={updatePostMutation}
            createPostMutation={createPostMutation}
          />
          {isNewWorkersRequest && (
            <Element>
              <Element mb={3}>
                <RequestMessage mutation={createWorkersRequestMutation} />
              </Element>
              <RequestButton
                mutation={createWorkersRequestMutation}
                color="primary"
                type="submit"
                mr={3}
                style={{ width: '100%' }}
                disabled={isSubmitDisabled}
                data-test="submit-workers-request-form"
              >
                {'Envoyer la Demande'}
              </RequestButton>
            </Element>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default WorkersRequestForm
